import {
  ON_CONVERSATION_UNENDED_EVENT_TYPE,
  ON_SESSION_TITLE_CHANGED_EVENT_TYPE,
} from 'core/constants';
import { runListenersForEvent } from 'core_main/globals/events';
import { getKustomerMainObject } from 'core_main/globals/helpers';
import { createEndConversationCallbackResponse } from 'core_main/sdk/end_conversation/helpers';
import { addSessionToStore, getStore } from 'core_main/store';
// eslint-disable-next-line import/no-cycle
import { importAndInitializePubNub } from 'core_main/globals/vendors';
import { handleError } from 'globals/errors';
import { subscribeToTypingIndicatorChannel } from 'core_main/helpers/typingIndicator';
import { PubNubSessionUpdateMessageEvent } from '../types';

// Timeout configuration constants for customer merge re-subscription
const INITIAL_DELAY_MS = 400; // Delay before attempting login after customer merge
const RETRY_INTERVAL_MS = 200; // Interval between connection retry attempts
const MAX_RETRIES = 10; // Maximum number of retry attempts (10 * 200ms = 2 seconds total)

// Store timeout IDs to prevent accumulation of timers from multiple customer merge events
let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
let initialTimeoutId: ReturnType<typeof setTimeout> | null = null;

/**
 * Re-subscribes to typing indicator channels for all active conversations after a customer merge.
 * This ensures that typing indicators continue to work with the new customer ID.
 */
const resubscribeTypingIndicatorChannels = () => {
  // Clear any existing retry timeout to prevent accumulation
  if (retryTimeoutId !== null) {
    clearTimeout(retryTimeoutId);
    retryTimeoutId = null;
  }

  const store = getStore();
  const sessions = store?.sessions || {};

  // Get all conversation IDs from sessions
  const conversationIds = Object.keys(sessions);

  if (conversationIds.length === 0) {
    return;
  }

  // Re-subscribe typing indicator channels for all active conversations
  // eslint-disable-next-line import/no-cycle
  importAndInitializePubNub()
    .then((pubnub) => {
      // Wait for PubNub to be connected before subscribing
      let retryCount = 0;

      const checkConnection = () => {
        const currentStore = getStore();
        if (currentStore?.isPubNubConnected) {
          // Clear retry timeout since we're done
          if (retryTimeoutId !== null) {
            clearTimeout(retryTimeoutId);
            retryTimeoutId = null;
          }
          // Subscribe to typing indicator channels for all conversations
          Promise.all(
            conversationIds.map((conversationId) =>
              subscribeToTypingIndicatorChannel(conversationId, pubnub),
            ),
          ).catch((error) => {
            handleError(error, {
              message:
                'Failed to re-subscribe typing indicator channels after customer merge',
            });
          });
        } else if (retryCount < MAX_RETRIES) {
          // Retry after a short delay if not connected yet
          retryCount++;
          retryTimeoutId = setTimeout(checkConnection, RETRY_INTERVAL_MS);
        } else {
          // Clear retry timeout since we're done (max retries reached)
          if (retryTimeoutId !== null) {
            clearTimeout(retryTimeoutId);
            retryTimeoutId = null;
          }
          handleError(
            new Error(
              'PubNub not connected after customer merge, skipping typing channel re-subscription',
            ),
            {
              message:
                'Failed to re-subscribe typing indicator channels - PubNub not connected',
            },
          );
        }
      };

      checkConnection();
    })
    .catch((error) => {
      // Clear retry timeout on error
      if (retryTimeoutId !== null) {
        clearTimeout(retryTimeoutId);
        retryTimeoutId = null;
      }
      handleError(error, {
        message:
          'Failed to initialize PubNub for re-subscribing typing indicator channels',
      });
    });
};

export const handleChatSessionUpdateEvent = (
  message: PubNubSessionUpdateMessageEvent,
) => {
  const lockedAtOp = message?.data?.changes?.attributes?.lockedAt?.op;

  const before = message?.data?.before;
  const after = message?.data?.after;

  const beforeCustomerId = before?.relationships?.customer?.data?.id;
  const afterCustomerId = after?.relationships?.customer?.data?.id;

  if (
    beforeCustomerId &&
    beforeCustomerId !== afterCustomerId &&
    afterCustomerId
  ) {
    // Clear any existing initial timeout to prevent accumulation
    if (initialTimeoutId !== null) {
      clearTimeout(initialTimeoutId);
      initialTimeoutId = null;
    }

    initialTimeoutId = setTimeout(() => {
      initialTimeoutId = null;
      const kustomerMain = getKustomerMainObject();
      const loginPromise = kustomerMain.login();

      // After login completes, re-subscribe to typing indicator channels
      if (loginPromise && typeof loginPromise.then === 'function') {
        loginPromise
          .then(() => {
            resubscribeTypingIndicatorChannels();
          })
          .catch((error) => {
            handleError(error, {
              message:
                'Login failed after customer merge, skipping typing channel re-subscription',
            });
          });
      } else {
        // If login doesn't return a promise, use the existing timeout approach
        resubscribeTypingIndicatorChannels();
      }
    }, INITIAL_DELAY_MS);
  }

  if (after?.id && after?.attributes) {
    const useLegacy = getStore().useLegacyConversationPreview === true;
    const sessionToStore = useLegacy
      ? { ...after, attributes: { ...after.attributes, name: undefined } }
      : after;
    addSessionToStore(sessionToStore);
    const previewDetailsNameBefore = (
      typeof before?.attributes?.previewDetails?.name === 'string'
        ? before.attributes.previewDetails.name
        : ''
    ).trim();
    const previewDetailsNameAfter = (
      typeof after.attributes?.previewDetails?.name === 'string'
        ? after.attributes.previewDetails.name
        : ''
    ).trim();
    const sessionTitleChanged =
      before?.attributes?.name !== after.attributes.name ||
      previewDetailsNameBefore !== previewDetailsNameAfter;
    if (!useLegacy && sessionTitleChanged) {
      runListenersForEvent(ON_SESSION_TITLE_CHANGED_EVENT_TYPE, after);
    }
  }

  if (!lockedAtOp) return;

  if (lockedAtOp === 'remove') {
    const callbackResponse = createEndConversationCallbackResponse(after);

    runListenersForEvent(ON_CONVERSATION_UNENDED_EVENT_TYPE, callbackResponse);
  }
};
